/**
 * STABBY — Harpoon Turret (doc 05). 1x1, 25 alloy, 180 HP, 12 dmg, 1.0/s, range 7.
 *
 * "Points at fish. Strongly."
 *
 * Silhouette family: HARPOON MAST. Wide plinth, a narrow housing that lifts the
 * works clear, and one long barbed spear in the brightest step of the ramp. At
 * gameplay zoom the spear IS the tower: you read what Stabby is doing by where
 * it points, so everything else is deliberately quiet and small.
 *
 * Signature: warm red targeting lamp on the yoke (towers red-warm, economy
 * gold-warm — both stay on the player's side of doc 10's light rule).
 */

import { facingOffset, tiersOf, type BuildContext, type EntityDraw } from '../entity.ts';
import { rampFor, sigRamp, signatureColor, SIGNATURE_TOWER } from '../materials.ts';
import { isoCylinder, isoDiamondRim, isoPrism, linePx, type EntityCanvas } from '../../render/primitives.ts';

/** Apparent spear reach in px — just inside the footprint, per Law L2. */
const SPEAR = 15;

/**
 * Upgrade motifs (doc 05's 3×3, doc 16 §1):
 *   A · SKEWER KEBAB  the spear itself — barbs, then reach, then a lance guard
 *   B · TRIGGER HAPPY feed drums on the housing, then a whirling cage
 *   C · MEAN BARBS    a venom rig behind the pivot, then tanks and a wet tip
 * Every motif is drawn from the same ramp the tower already uses: Law L5 caps
 * an entity at 12 colours no matter how upgraded it is.
 */
export const drawStabby: EntityDraw = (c: EntityCanvas, ctx: BuildContext): void => {
  const r = rampFor(ctx.depth);
  const sig = sigRamp(SIGNATURE_TOWER);
  const lamp = signatureColor(sig, ctx.state);
  const up = tiersOf(ctx);

  // Firing kicks the spear back along its own axis by 2 px and eases out — a
  // recoil readable at 1x that never leaves the pixel grid (Law L4).
  const recoil = ctx.state === 'firing' ? 2 - Math.round(2 * ctx.phase) : 0;

  // --- plinth ---------------------------------------------------------------
  const plinthH = 2;
  isoPrism(c, { n: 1, inset: 1, height: plinthH, ramp: r });
  isoDiamondRim(c.s, c.ax, c.ay - plinthH, 7, r.edge);

  // --- housing --------------------------------------------------------------
  // Tall and narrow on purpose: it lifts the pivot high enough that a spear
  // pointing at the camera still clears the plinth instead of vanishing into it.
  const housingH = 12;
  isoPrism(c, { n: 1, inset: 4, height: housingH, lift: plinthH, ramp: r });
  const housingTop = plinthH + housingH; // 14 px above the anchor
  c.s.fillRect(c.ax - 5, c.ay - housingTop + 4, 10, 1, r.deep); // service band
  c.s.fillRect(c.ax - 4, c.ay - housingTop + 7, 2, 3, r.deep); // vent
  c.s.fillRect(c.ax + 1, c.ay - housingTop + 7, 2, 3, r.deep);

  // collar step, so the housing and the yoke never read as one cylinder
  isoPrism(c, { n: 1, inset: 6, height: 2, lift: housingTop, ramp: r });

  // --- yoke -----------------------------------------------------------------
  const yokeTop = isoCylinder(c, { rx: 6, height: 3, lift: housingTop + 2, ramp: r });
  const pivotY = yokeTop + 1;

  // An idle tower parks its spear 3 px lower than a tracking one: the state is
  // legible from the silhouette alone, not just from the lamp. The placement
  // ghost parks too — it previews the tower you get, which starts idle.
  const rest =
    ctx.state === 'idle' || ctx.state === 'dry' || ctx.state === 'ghost' ? 3 : 0;
  // path A buys reach: the capstone spear is visibly a different weapon
  const reach = SPEAR + (up.a >= 2 ? 3 : 0) + (up.a >= 3 ? 5 : 0);
  const dir = facingOffset(ctx.facing, reach);
  const back = facingOffset(ctx.facing, 6);
  const kick = { x: Math.sign(dir.x) * recoil, y: Math.sign(dir.y) * recoil };
  const tipX = c.ax + dir.x - kick.x;
  const tipY = pivotY + dir.y - kick.y + rest;

  const drawSpear = (): void => {
    // Counterweight sits behind the pivot, on the far side of the yoke.
    c.s.fillRect(c.ax - back.x - 2 + kick.x, pivotY - back.y - 2 + kick.y, 5, 4, r.left);
    c.s.fillRect(c.ax - back.x - 2 + kick.x, pivotY - back.y - 2 + kick.y, 5, 1, r.edge);

    // Shaft: bright ridge over a dark underside, so it reads as a rod at 1x.
    linePx(c.s, c.ax + kick.x, pivotY + 1 + kick.y, tipX, tipY + 1, r.deep, 3);
    linePx(c.s, c.ax + kick.x, pivotY + kick.y, tipX, tipY, r.edge, 2);

    // Head: a barbed point swept back along the shaft.
    const bx = Math.round(dir.x / 4);
    const by = Math.round(dir.y / 4);
    linePx(c.s, tipX, tipY, tipX - bx - by, tipY - by + Math.round(bx / 2), r.edge, 1);
    linePx(c.s, tipX, tipY, tipX - bx + by, tipY - by - Math.round(bx / 2), r.edge, 1);
    c.s.fillRect(tipX - 1, tipY - 1, 3, 2, r.edge);

    // A · SKEWER KEBAB — one extra barb pair per tier, walked back down the shaft
    for (let k = 0; k < up.a; k++) {
      const t = 0.62 - k * 0.16; // how far along the shaft this pair sits
      const px = Math.round(c.ax + kick.x + (tipX - c.ax - kick.x) * t);
      const py = Math.round(pivotY + kick.y + (tipY - pivotY - kick.y) * t);
      c.s.fillRect(px - 2, py - 1, 5, 1, r.edge);
      c.s.px(px - 2, py, r.left);
      c.s.px(px + 2, py, r.left);
    }
    // the capstone adds a lance guard at the pivot: pure silhouette delta
    if (up.a >= 3) {
      const gx = c.ax + Math.round(dir.x / 6) + kick.x;
      const gy = pivotY + Math.round(dir.y / 6) + kick.y;
      c.s.fillRect(gx - 3, gy - 3, 7, 2, r.edge);
      c.s.fillRect(gx - 2, gy - 1, 5, 3, r.left);
      c.s.px(gx - 4, gy - 2, r.edge);
      c.s.px(gx + 4, gy - 2, r.edge);
    }
  };

  // Painter's order around the yoke: a spear pointing away sits behind it.
  if (dir.y < 0) {
    drawSpear();
    isoCylinder(c, { rx: 6, height: 3, lift: housingTop + 2, ramp: r });
  } else {
    drawSpear();
  }

  if (lamp !== null) {
    const lx = c.ax + Math.round(dir.x / 5);
    const ly = pivotY + Math.round(dir.y / 5) + 1;
    c.s.fillRect(lx - 1, ly, 3, 2, lamp);
    if (ctx.state === 'firing') {
      c.s.px(lx - 2, ly, sig.halo);
      c.s.px(lx + 3, ly, sig.halo);
      c.s.px(lx, ly - 1, sig.halo);
    }
  }

  // --- B · TRIGGER HAPPY: feed drums, then the blender cage -----------------
  if (up.b >= 1) {
    isoCylinder(c, { rx: 3, height: 4, lift: plinthH + 3, dx: -7, dy: 2, ramp: r });
    c.s.fillRect(c.ax - 9, c.ay - plinthH - 6, 5, 1, r.edge);
  }
  if (up.b >= 2) {
    isoCylinder(c, { rx: 3, height: 4, lift: plinthH + 3, dx: 7, dy: 2, ramp: r });
    for (let k = 0; k < 3; k++) c.s.fillRect(c.ax - 3, c.ay - housingTop + 1 + k * 3, 6, 1, r.edge);
  }
  if (up.b >= 3) {
    // "Beluga Blender": a cage around the yoke, stepping two frames while firing
    const spin = ctx.state === 'firing' ? Math.round(ctx.phase * 2) % 2 : 0;
    for (let k = 0; k < 4; k++) {
      const a = k * 2 + spin;
      const dx = [9, 6, 0, -6, -9, -6, 0, 6][a % 8]!;
      const dy = [0, 3, 4, 3, 0, -3, -4, -3][a % 8]!;
      c.s.fillRect(c.ax + dx - 1, pivotY + dy, 2, 6, r.left);
      c.s.px(c.ax + dx, pivotY + dy - 1, r.edge);
    }
    isoDiamondRim(c.s, c.ax, pivotY + 3, 8, r.edge);
  }

  // --- C · MEAN BARBS: the venom rig -----------------------------------------
  if (up.c >= 1) {
    const bx = c.ax - back.x;
    const by = pivotY - Math.round(back.y / 2) + 1;
    c.s.fillRect(bx - 2, by, 4, 4, r.left);
    c.s.fillRect(bx - 2, by, 4, 1, r.edge);
  }
  if (up.c >= 2) {
    // hose from the canister along the shaft
    linePx(c.s, c.ax - back.x, pivotY - Math.round(back.y / 2) + 2, c.ax + Math.round(dir.x / 3), pivotY + Math.round(dir.y / 3), r.deep, 1);
    c.s.px(tipX, tipY + 2, sig.on);
  }
  if (up.c >= 3) {
    // "Tetanus Express": twin tanks flanking the housing, and a wet tip
    for (const side of [-1, 1]) {
      isoCylinder(c, { rx: 3, height: 11, lift: plinthH, dx: side * 8, dy: -1, ramp: r });
      c.s.px(c.ax + side * 8, c.ay - plinthH - 13, sig.on);
    }
    // manifold across the housing, tying the tanks together
    c.s.fillRect(c.ax - 8, c.ay - housingTop + 2, 17, 2, r.left);
    c.s.fillRect(c.ax - 8, c.ay - housingTop + 2, 17, 1, r.edge);
    c.s.fillRect(tipX - 1, tipY + 1, 3, 2, sig.hot);
  }

  // Ammo-dry: the spear rack on the housing is empty, and a dark bar shows it.
  if (ctx.state === 'dry') {
    c.s.fillRect(c.ax - 4, c.ay - housingTop - 1, 8, 1, r.deep);
  }
};
